<script setup lang="ts">
import { watch } from "vue";
import { useTranslation } from "i18next-vue";
import { useGetMemoryWarningInfo } from "@/composable/useMemoryWarning";
import { reloadApp } from "@/utils/reloadApp";
import { Logger } from "@/logger";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import { Z_INDEX_ALERT } from "./layouts/constants";
import { canvasTopForTexts } from "@/stores/CanvasLayoutState";

const props = defineProps<{
  fullscreen: boolean;
}>();

const { t } = useTranslation();
const {
  showBanner,
  dismiss,
  percentageOfLimit,
  sessionDeltaMb,
  usingPercentage,
} = useGetMemoryWarningInfo();

// Trace l'apparition côté Sentry : c'est le point de corrélation entre une
// plainte de lenteur et la mesure mémoire réelle au moment où elle a lieu.
watch(showBanner, (visible) => {
  if (!visible) return;
  Logger.breadcrumb("memory.canvas-alert-shown", {
    percent_of_limit: usingPercentage.value
      ? Math.round(percentageOfLimit.value as number)
      : null,
    session_delta_mb:
      sessionDeltaMb.value === null ? null : Math.round(sessionDeltaMb.value),
  });
});

async function saveAndReload() {
  dismiss();
  await reloadApp();
}
</script>

<template>
  <div
    v-if="showBanner"
    class="memory-alert canvas-text-scaled"
    :style="{
      top: `${props.fullscreen ? 12 : canvasTopForTexts + 12}px`,
      zIndex: Z_INDEX_ALERT,
    }"
    role="alert"
  >
    <div class="alert-header">
      <PhosphorIcon name="warning" :size="18" weight="fill" />
      <strong>{{ t("canvas:memory.alert.title") }}</strong>
      <button
        class="alert-close"
        @click="dismiss"
        :aria-label="t('canvas:memory.alert.dismiss')"
      >
        <PhosphorIcon name="x" :size="12" />
      </button>
    </div>

    <p class="alert-message">{{ t("canvas:memory.alert.message") }}</p>

    <button class="alert-action" @click="saveAndReload">
      <i class="bi bi-arrow-clockwise"></i>
      <span>{{ t("canvas:memory.alert.action") }}</span>
    </button>
  </div>
</template>

<style lang="scss" scoped>
.memory-alert {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: min(26rem, calc(100vw - 8rem));
  padding: 0.85rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid var(--bs-danger);
  background-color: var(--canvas-bg, #1c1f26);
  color: #fff;
  box-shadow: 0 8px 24px rgb(0 0 0 / 45%);
}

.alert-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--bs-danger);
  font-size: 0.95rem;
}

.alert-close {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  background: none;
  border: none;
  padding: 0.15rem;
  color: #fff;
  opacity: 0.6;
  cursor: pointer;

  &:hover {
    opacity: 1;
  }
}

.alert-message {
  margin: 0.5rem 0 0.75rem;
  font-size: 0.8rem;
  line-height: 1.45;
  opacity: 0.9;
}

.alert-action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  width: 100%;
  padding: 0.45rem 0.85rem;
  border: none;
  border-radius: 0.375rem;
  background-color: var(--bs-danger);
  color: #fff;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;

  &:hover {
    filter: brightness(1.1);
  }
}
</style>
